import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  CircularProgress,
  FormControlLabel,
  Checkbox,
  Alert,
  TextField,
  useTheme,
  useMediaQuery,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import api from '../services/api';
import { Event } from '../types';
import { useNotification } from '../context/NotificationContext';

interface ExcelImportDialogProps {
  open: boolean;
  onClose: () => void;
  events: Event[];
  onImportComplete?: () => void;
}

interface PreviewRow {
  rowNumber: number;
  categoryString?: string;
  collective?: string;
  danceName?: string;
  participantsCount?: number;
  errors: string[];
  parsed?: {
    disciplineName?: string;
    nominationName?: string;
    ageName?: string;
    categoryName?: string;
  };
}

export const ExcelImportDialog: React.FC<ExcelImportDialogProps> = ({
  open,
  onClose,
  events,
  onImportComplete,
}) => {
  const { showSuccess, showError } = useNotification();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [selectedEventId, setSelectedEventId] = useState<number | ''>('');
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<PreviewRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [deleteExisting, setDeleteExisting] = useState(false);
  const [importResult, setImportResult] = useState<{
    imported: number;
    skipped: number;
    errors: Array<{ row: number; error: string }>;
  } | null>(null);
  const [errorEditOpen, setErrorEditOpen] = useState(false);
  const [editableErrorRows, setEditableErrorRows] = useState<PreviewRow[]>([]);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];
    if (selectedFile) {
      setFile(selectedFile);
      setPreview([]);
      setImportResult(null);
    }
  };

  const handlePreview = async () => {
    if (!file || !selectedEventId) {
      showError('Выберите файл и мероприятие');
      return;
    }

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('eventId', String(selectedEventId));
      formData.append('dryRun', 'true');

      const response = await api.post('/api/excel-import', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      setPreview(response.data.preview || []);
      if (response.data.errors > 0) {
        showError(`Найдено ${response.data.errors} строк с ошибками`);
      }
    } catch (error: any) {
      console.error('Error previewing file:', error);
      showError(error.response?.data?.error || 'Ошибка предпросмотра файла');
    } finally {
      setLoading(false);
    }
  };

  const handleImport = async () => {
    if (!file || !selectedEventId) {
      showError('Выберите файл и мероприятие');
      return;
    }

    if (preview.length === 0) {
      showError('Сначала выполните предпросмотр');
      return;
    }

    setImporting(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('eventId', String(selectedEventId));
      formData.append('dryRun', 'false');
      formData.append('deleteExisting', String(deleteExisting));

      const response = await api.post('/api/excel-import', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      setImportResult({
        imported: response.data.imported,
        skipped: response.data.skipped,
        errors: response.data.errors || [],
      });

      if (response.data.errors && response.data.errors.length > 0) {
        const errorRowsMap = new Set<number>(response.data.errors.map((e: any) => e.row));
        const rowsToEdit = preview.filter((row) => errorRowsMap.has(row.rowNumber));
        setEditableErrorRows(rowsToEdit);
        setErrorEditOpen(true);
      }

      if (response.data.imported > 0) {
        if (response.data.skipped > 0) {
          showSuccess(`Импортировано ${response.data.imported} регистраций. ${response.data.skipped} записей с ошибками сохранены в разделе "Ошибки импорта" для последующего исправления.`);
        } else {
          showSuccess(`Успешно импортировано ${response.data.imported} регистраций`);
        }
      } else if (response.data.skipped > 0) {
        showSuccess(`${response.data.skipped} записей с ошибками сохранены в разделе "Ошибки импорта" для последующего исправления.`);
      }

      if (onImportComplete) {
        onImportComplete();
      }
    } catch (error: any) {
      console.error('Error importing file:', error);
      console.error('Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        statusText: error.response?.statusText,
      });
      
      // Более детальное сообщение об ошибке
      let errorMessage = 'Ошибка импорта файла';
      
      if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
        if (error.response.data.details) {
          errorMessage += ` (${error.response.data.details})`;
        }
        if (error.response.data.message) {
          errorMessage += `. ${error.response.data.message}`;
        }
      } else if (error.message) {
        errorMessage = error.message;
      } else if (error.response?.status === 413) {
        errorMessage = 'Файл слишком большой. Максимальный размер: 100 МБ';
      } else if (error.response?.status === 400) {
        errorMessage = 'Неверный формат файла или отсутствуют обязательные параметры';
      } else if (error.response?.status === 500) {
        errorMessage = 'Внутренняя ошибка сервера. Проверьте логи сервера';
      } else if (!error.response) {
        errorMessage = 'Не удалось подключиться к серверу. Проверьте подключение к интернету';
      }
      
      showError(errorMessage);
    } finally {
      setImporting(false);
    }
  };

  const handleClose = () => {
    setFile(null);
    setPreview([]);
    setImportResult(null);
    setDeleteExisting(false);
    setErrorEditOpen(false);
    setEditableErrorRows([]);
    onClose();
  };

  const handleErrorRowChange = (index: number, field: keyof PreviewRow | 'disciplineName' | 'nominationName' | 'ageName' | 'categoryName') => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = event.target.value;
    setEditableErrorRows((prev) => {
      const copy = [...prev];
      const row = { ...copy[index] };
      if (field === 'disciplineName' || field === 'nominationName' || field === 'ageName' || field === 'categoryName') {
        row.parsed = row.parsed || {};
        (row.parsed as any)[field] = value;
      } else {
        (row as any)[field] = value;
      }
      copy[index] = row;
      return copy;
    });
  };

  const handleImportCorrectedRows = async () => {
    if (!selectedEventId || editableErrorRows.length === 0) {
      return;
    }

    try {
      const rowsPayload = editableErrorRows.map((row) => ({
        rowNumber: row.rowNumber,
        collective: row.collective || '',
        danceName: row.danceName || '',
        participantsCount: row.participantsCount || 0,
        disciplineName: row.parsed?.disciplineName || '',
        nominationName: row.parsed?.nominationName || '',
        ageName: row.parsed?.ageName || '',
        categoryName: row.parsed?.categoryName || '',
      }));

      const response = await api.post('/api/excel-import/manual', {
        eventId: selectedEventId,
        rows: rowsPayload,
      });

      const imported = response.data.imported || 0;
      if (imported > 0) {
        showSuccess(`Дополнительно импортировано ${imported} исправленных строк`);
      }
      if (response.data.errors && response.data.errors.length > 0) {
        showError(`Не удалось импортировать ${response.data.errors.length} строк. Проверьте данные.`);
      }

      if (onImportComplete) {
        onImportComplete();
      }

      setErrorEditOpen(false);
    } catch (error: any) {
      console.error('Error importing corrected rows:', error);
      showError(error.response?.data?.error || 'Ошибка импорта исправленных строк');
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth fullScreen={isMobile}>
      <DialogTitle>Импорт регистраций из Excel</DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {/* Справка по формату таблицы */}
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <HelpOutlineIcon color="primary" />
                <Typography variant="subtitle1" fontWeight="medium">
                  Справка по формату таблицы для импорта
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Таблица должна содержать следующие столбцы (все столбцы опциональны, кроме отмеченных):
              </Typography>
              <TableContainer component={Paper} variant="outlined" sx={{ overflowX: 'auto' }}>
                <Table size={isMobile ? 'small' : 'medium'}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold', width: '80px' }}>Столбец</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Название</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Описание</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', width: '100px' }}>Обязательно</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow>
                      <TableCell>A</TableCell>
                      <TableCell>Номер/Категория</TableCell>
                      <TableCell>
                        Может содержать строку категории вида "1. Jazz Соло Бэби Beginners" 
                        (номер блока, дисциплина, номинация, возраст, категория). 
                        Если категория указана в отдельной строке, она применяется ко всем последующим строкам до следующей категории.
                      </TableCell>
                      <TableCell>Нет*</TableCell>
                    </TableRow>
                    <TableRow sx={{ bgcolor: 'error.light', opacity: 0.1 }}>
                      <TableCell>B</TableCell>
                      <TableCell><strong>Коллектив</strong></TableCell>
                      <TableCell>Название коллектива</TableCell>
                      <TableCell><strong>Да</strong></TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>C</TableCell>
                      <TableCell>Название танца</TableCell>
                      <TableCell>Название номера. Может быть пустым для соло (в этом случае используется название коллектива)</TableCell>
                      <TableCell>Нет</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>D</TableCell>
                      <TableCell>Количество участников</TableCell>
                      <TableCell>Число участников в номере</TableCell>
                      <TableCell>Нет</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>E</TableCell>
                      <TableCell>-</TableCell>
                      <TableCell>Не используется (пустая колонка)</TableCell>
                      <TableCell>-</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>F</TableCell>
                      <TableCell>Руководители</TableCell>
                      <TableCell>ФИО руководителей (можно несколько через запятую)</TableCell>
                      <TableCell>Нет</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>G</TableCell>
                      <TableCell>-</TableCell>
                      <TableCell>Не используется (пустая колонка)</TableCell>
                      <TableCell>-</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>H</TableCell>
                      <TableCell>Тренеры</TableCell>
                      <TableCell>ФИО тренеров (можно несколько через запятую)</TableCell>
                      <TableCell>Нет</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>I</TableCell>
                      <TableCell>-</TableCell>
                      <TableCell>Не используется (пустая колонка)</TableCell>
                      <TableCell>-</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>J</TableCell>
                      <TableCell>Школа</TableCell>
                      <TableCell>Название школы/студии</TableCell>
                      <TableCell>Нет</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>K</TableCell>
                      <TableCell>Контакты</TableCell>
                      <TableCell>Контактная информация (телефон, email и т.д.)</TableCell>
                      <TableCell>Нет</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>L</TableCell>
                      <TableCell>Город</TableCell>
                      <TableCell>Город коллектива</TableCell>
                      <TableCell>Нет</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>M</TableCell>
                      <TableCell>Длительность</TableCell>
                      <TableCell>Длительность номера в формате HH:MM:SS или MM:SS</TableCell>
                      <TableCell>Нет</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>N</TableCell>
                      <TableCell>-</TableCell>
                      <TableCell>Не используется (длительность с перерывами)</TableCell>
                      <TableCell>-</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>O</TableCell>
                      <TableCell>Видео URL</TableCell>
                      <TableCell>Ссылка на видео номера</TableCell>
                      <TableCell>Нет</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>P</TableCell>
                      <TableCell>-</TableCell>
                      <TableCell>Не используется (примечание)</TableCell>
                      <TableCell>-</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>Q</TableCell>
                      <TableCell>ФИО на дипломы</TableCell>
                      <TableCell>
                        Список ФИО участников для дипломов. Каждое ФИО на новой строке. 
                        Количество дипломов определяется автоматически по количеству строк с русскими символами.
                      </TableCell>
                      <TableCell>Нет</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>R</TableCell>
                      <TableCell>Количество медалей</TableCell>
                      <TableCell>Число медалей для номера</TableCell>
                      <TableCell>Нет</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
              <Alert severity="info" sx={{ mt: 2 }}>
                <Typography variant="body2" component="div">
                  <strong>Важно:</strong>
                  <ul style={{ marginTop: '8px', marginBottom: '0', paddingLeft: '20px' }}>
                    <li>Столбец B (Коллектив) обязателен для всех строк с данными регистрации</li>
                    <li>Категория (столбец A) может быть указана в отдельной строке перед группой регистраций</li>
                    <li>Если категория указана в строке вида "1. Jazz Соло Бэби Beginners", она применяется ко всем последующим строкам</li>
                    <li>Для определения дисциплины, номинации, возраста и категории система использует категорию из столбца A</li>
                    <li>Если категория не указана или не распознана, строка будет помечена как содержащая ошибки</li>
                    <li>Строки с ошибками сохраняются в разделе "Ошибки импорта" для последующего исправления</li>
                  </ul>
                </Typography>
              </Alert>
            </AccordionDetails>
          </Accordion>

          <FormControl fullWidth>
            <InputLabel>Мероприятие</InputLabel>
            <Select
              value={selectedEventId}
              label="Мероприятие"
              onChange={(e) => setSelectedEventId(e.target.value as number)}
            >
              {events.map((event) => (
                <MenuItem key={event.id} value={event.id}>
                  {event.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Box>
            <input
              accept=".xlsx,.xls"
              style={{ display: 'none' }}
              id="excel-file-upload"
              type="file"
              onChange={handleFileChange}
            />
            <label htmlFor="excel-file-upload">
              <Button
                variant="outlined"
                component="span"
                startIcon={<CloudUploadIcon />}
                fullWidth
              >
                {file ? file.name : 'Выберите Excel файл'}
              </Button>
            </label>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              Формат: .xlsx, .xls (максимум 100 MB)
            </Typography>
          </Box>

          {file && selectedEventId && (
            <Box>
              <Button
                variant="contained"
                onClick={handlePreview}
                disabled={loading}
                sx={{ mb: 2 }}
              >
                {loading ? <CircularProgress size={20} /> : 'Предпросмотр'}
              </Button>
            </Box>
          )}

          {preview.length > 0 && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Предпросмотр ({preview.length} строк)
              </Typography>
              <TableContainer component={Paper} sx={{ maxHeight: 400, overflowX: 'auto' }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Строка</TableCell>
                      <TableCell>Коллектив</TableCell>
                      <TableCell>Название</TableCell>
                      <TableCell>Дисциплина</TableCell>
                      <TableCell>Номинация</TableCell>
                      <TableCell>Возраст</TableCell>
                      <TableCell>Категория</TableCell>
                      <TableCell>Участники</TableCell>
                      <TableCell>Ошибки</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {preview.map((row) => (
                      <TableRow key={row.rowNumber}>
                        <TableCell>{row.rowNumber}</TableCell>
                        <TableCell>{row.collective || '-'}</TableCell>
                        <TableCell>{row.danceName || '-'}</TableCell>
                        <TableCell>{row.parsed?.disciplineName || '-'}</TableCell>
                        <TableCell>{row.parsed?.nominationName || '-'}</TableCell>
                        <TableCell>{row.parsed?.ageName || '-'}</TableCell>
                        <TableCell>{row.parsed?.categoryName || '-'}</TableCell>
                        <TableCell>{row.participantsCount || '-'}</TableCell>
                        <TableCell>
                          {row.errors.length > 0 ? (
                            <Chip
                              label={`${row.errors.length} ошибок`}
                              color="error"
                              size="small"
                            />
                          ) : (
                            <Chip label="OK" color="success" size="small" />
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {preview.some((r) => r.errors.length > 0) && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  Некоторые строки содержат ошибки. Они будут сохранены в разделе "Ошибки импорта" в настройках мероприятия для последующего исправления и импорта.
                </Alert>
              )}

              <FormControlLabel
                control={
                  <Checkbox
                    checked={deleteExisting}
                    onChange={(e) => setDeleteExisting(e.target.checked)}
                  />
                }
                label="Удалить существующие регистрации мероприятия перед импортом"
                sx={{ mt: 2 }}
              />

              <Button
                variant="contained"
                color="primary"
                onClick={handleImport}
                disabled={importing}
                fullWidth
                sx={{ mt: 2 }}
              >
                {importing ? (
                  <>
                    <CircularProgress size={20} sx={{ mr: 1 }} />
                    Импорт...
                  </>
                ) : (
                  'Импортировать'
                )}
              </Button>
            </Box>
          )}

          {importResult && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Результаты импорта
              </Typography>
              <Typography>Импортировано: {importResult.imported}</Typography>
              <Typography>Пропущено: {importResult.skipped}</Typography>
              {importResult.errors.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2">Ошибки:</Typography>
                  {importResult.errors.slice(0, 10).map((error, index) => (
                    <Typography key={index} variant="body2" color="error">
                      Строка {error.row}: {error.error}
                    </Typography>
                  ))}
                  {importResult.errors.length > 10 && (
                    <Typography variant="body2" color="text.secondary">
                      ... и еще {importResult.errors.length - 10} ошибок
                    </Typography>
                  )}
                </Box>
              )}
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Закрыть</Button>
      </DialogActions>

      {/* Диалог редактирования строк с ошибками */}
      <Dialog open={errorEditOpen} onClose={() => setErrorEditOpen(false)} maxWidth="lg" fullWidth fullScreen={isMobile}>
        <DialogTitle>Исправление строк с ошибками</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Вы можете исправить поля и импортировать только эти строки без повторного загрузки файла.
          </Typography>
          <TableContainer component={Paper} sx={{ maxHeight: 400, overflowX: 'auto' }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Строка</TableCell>
                  <TableCell>Коллектив</TableCell>
                  <TableCell>Название</TableCell>
                  <TableCell>Дисциплина</TableCell>
                  <TableCell>Номинация</TableCell>
                  <TableCell>Возраст</TableCell>
                  <TableCell>Категория</TableCell>
                  <TableCell>Участники</TableCell>
                  <TableCell>Ошибки</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {editableErrorRows.map((row, index) => (
                  <TableRow key={row.rowNumber}>
                    <TableCell>{row.rowNumber}</TableCell>
                    <TableCell>
                      <TextField
                        value={row.collective || ''}
                        onChange={handleErrorRowChange(index, 'collective')}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        value={row.danceName || ''}
                        onChange={handleErrorRowChange(index, 'danceName')}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        value={row.parsed?.disciplineName || ''}
                        onChange={handleErrorRowChange(index, 'disciplineName')}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        value={row.parsed?.nominationName || ''}
                        onChange={handleErrorRowChange(index, 'nominationName')}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        value={row.parsed?.ageName || ''}
                        onChange={handleErrorRowChange(index, 'ageName')}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        value={row.parsed?.categoryName || ''}
                        onChange={handleErrorRowChange(index, 'categoryName')}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        value={row.participantsCount ?? ''}
                        onChange={handleErrorRowChange(index, 'participantsCount')}
                        size="small"
                        type="number"
                      />
                    </TableCell>
                    <TableCell>
                      {row.errors && row.errors.length > 0 ? (
                        <Typography variant="caption" color="error">
                          {row.errors.join('; ')}
                        </Typography>
                      ) : (
                        <Chip label="OK" color="success" size="small" />
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setErrorEditOpen(false)}>Отмена</Button>
          <Button variant="contained" onClick={handleImportCorrectedRows} disabled={editableErrorRows.length === 0}>
            Импортировать исправленные
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};


